import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { saveAs } from "file-saver";
import { CiSearch } from "react-icons/ci";
import { FiDownload, FiEye } from "react-icons/fi";
import { TailSpin } from "react-loader-spinner";

import { DataTable, type BaseRowItem } from "../../components/data-table";
import {
  SURVEY_RESPONSES_LIMIT,
  useSurveyResponsesQuery,
  type SurveyQuestion,
  type SurveyResponseApi,
} from "../../lib/api";
import { exportSurveyResponses } from "../../lib/api/surveysClient";
import ResponseDetailModal from "./ResponseDetailModal";
import {
  formatDuration,
  formatOrdinalDate,
  placementLabel,
  respondentName,
  surveyErrorMessage,
} from "./surveyTypes";

type Props = {
  surveyId: string;
  questions: SurveyQuestion[];
  /** Used only to name the exported file. */
  surveyTitle?: string;
};

type ResponseRow = BaseRowItem & {
  raw: SurveyResponseApi;
  name: string;
  email: string | null;
  statusLabel: string;
  isComplete: boolean;
  answeredLabel: string;
  placementLabel: string;
  durationLabel: string;
  dateLabel: string;
};

const STATUS_TABS = [
  { id: "", label: "All" },
  { id: "COMPLETED", label: "Completed" },
  { id: "PARTIAL", label: "Partial" },
];

const SurveyResponsesTab: React.FC<Props> = ({
  surveyId,
  questions,
  surveyTitle,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<SurveyResponseApi | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const { data, isPending, isError, error, refetch, isFetching } =
    useSurveyResponsesQuery(surveyId, {
      page,
      limit: SURVEY_RESPONSES_LIMIT,
      search,
      status,
    });

  const responses = useMemo(() => data?.responses ?? [], [data]);
  const pagination = data?.pagination;

  const rows = useMemo<ResponseRow[]>(
    () =>
      responses.map((r) => {
        const answered = r.answers.length;
        return {
          id: r._id,
          raw: r,
          name: respondentName(r.user),
          email: r.user?.email ?? null,
          statusLabel: r.status === "COMPLETED" ? "Completed" : "Partial",
          isComplete: r.status === "COMPLETED",
          answeredLabel: `${answered}/${questions.length}`,
          placementLabel: r.placement ? placementLabel(r.placement) : "—",
          durationLabel: formatDuration(r.durationSeconds),
          dateLabel: formatOrdinalDate(r.submittedAt ?? r.startedAt),
        };
      }),
    [responses, questions.length]
  );

  const openDetail = useCallback(
    (row: ResponseRow) => setSelected(row.raw),
    []
  );

  const columns = useMemo<ColumnDef<ResponseRow, unknown>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Respondent",
        cell: ({ row }) => (
          <div className="min-w-[160px]">
            <p className="font-medium text-white">{row.original.name}</p>
            {row.original.email && (
              <p className="mt-0.5 truncate text-[11px] text-[#869AA9]">
                {row.original.email}
              </p>
            )}
          </div>
        ),
      },
      {
        accessorKey: "statusLabel",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
              row.original.isComplete
                ? "border-emerald-500/35 bg-emerald-500/15 text-emerald-400"
                : "border-amber-500/35 bg-amber-500/15 text-amber-300"
            }`}
          >
            {row.original.statusLabel}
          </span>
        ),
      },
      {
        accessorKey: "answeredLabel",
        header: "Answered",
        cell: ({ getValue }) => (
          <span className="tabular-nums text-adminTable-muted">
            {String(getValue())}
          </span>
        ),
      },
      {
        accessorKey: "placementLabel",
        header: "Came from",
        meta: { hideBelow: "lg" as const },
        cell: ({ getValue }) => (
          <span className="text-adminTable-muted">{String(getValue())}</span>
        ),
      },
      {
        accessorKey: "durationLabel",
        header: "Time",
        meta: { hideBelow: "md" as const },
        cell: ({ getValue }) => (
          <span className="tabular-nums text-adminTable-muted">
            {String(getValue())}
          </span>
        ),
      },
      {
        accessorKey: "dateLabel",
        header: "Date",
        meta: { hideBelow: "md" as const },
        cell: ({ getValue }) => (
          <span className="text-adminTable-muted">{String(getValue())}</span>
        ),
      },
      {
        id: "actions",
        header: "",
        meta: { align: "right" as const },
        cell: ({ row }) => (
          <button
            type="button"
            aria-label={`View response from ${row.original.name}`}
            onClick={() => openDetail(row.original)}
            className="rounded-lg border border-[#3A4852] bg-[#1C2227] p-2 text-[#AEBCC6] transition hover:border-secondary_button hover:text-white"
          >
            <FiEye className="text-base" />
          </button>
        ),
      },
    ],
    [openDetail]
  );

  /**
   * The server streams the CSV already built, so this only saves the bytes. The status tabs
   * are deliberately not passed through — the export endpoint takes no status filter, so an
   * export always covers every response the search matches, partials included.
   */
  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setExportError("");
    try {
      const blob = await exportSurveyResponses(surveyId, { search });
      const slug =
        (surveyTitle ?? "survey")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 60) || "survey";
      saveAs(blob, `${slug}-responses.csv`);
    } catch (err) {
      setExportError(surveyErrorMessage(err, "Could not export responses."));
    } finally {
      setExporting(false);
    }
  }, [exporting, surveyId, search, surveyTitle]);

  const hasResponses = (pagination?.total ?? rows.length) > 0;

  return (
    <section className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border border-[#2C363E] bg-[#13181B] px-3">
          <CiSearch className="shrink-0 text-[22px] text-[#869AA9]" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full bg-transparent text-[13px] text-[#E7EBEE] outline-none placeholder:text-[#5C6B76]"
          />
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || !hasResponses}
          title={hasResponses ? undefined : "No responses to export yet"}
          className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-[#3A4852] bg-[#1C2227] px-4 text-[12px] font-semibold text-[#C6D0D7] transition hover:border-secondary_button hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          {exporting ? (
            <TailSpin color="#C6D0D7" height={16} width={16} />
          ) : (
            <FiDownload className="text-base" />
          )}
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {exportError && (
        <p className="mb-3 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-2.5 text-[12px] text-red-100/95">
          {exportError}
        </p>
      )}

      <div
        className="mb-4 flex flex-wrap gap-2"
        role="tablist"
        aria-label="Response status"
      >
        {STATUS_TABS.map((tab) => {
          const active = status === tab.id;
          return (
            <button
              key={tab.id || "all"}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStatus(tab.id)}
              className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition ${
                active
                  ? "border-secondary_button bg-secondary_button text-white"
                  : "border-[#2C363E] bg-[#1C2227] text-[#869AA9] hover:border-[#3A4852] hover:text-[#C6D0D7]"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {isFetching && !isPending && (
        <p className="mb-2 text-[12px] text-[#869AA9]">Updating…</p>
      )}

      {isError && (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
          <span>{surveyErrorMessage(error, "Could not load responses.")}</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
          >
            Retry
          </button>
        </div>
      )}

      {isPending && !isError ? (
        <div className="flex justify-center py-16">
          <TailSpin color="#0B75C2" height={36} width={36} />
        </div>
      ) : (
        !isError && (
          <DataTable<ResponseRow>
            columns={columns}
            data={rows}
            currentPage={page}
            setCurrentPage={setPage}
            totalPage={Math.max(1, pagination?.pages ?? 1)}
            count={pagination?.total ?? rows.length}
            limit={SURVEY_RESPONSES_LIMIT}
            onRowClick={openDetail}
            emptyContent="No responses match this filter."
            className="overflow-hidden rounded-xl border border-[#2C363E]"
            fixedBodyHeight="min(560px, 55vh)"
          />
        )
      )}

      <ResponseDetailModal
        open={Boolean(selected)}
        response={selected}
        questions={questions}
        onClose={() => setSelected(null)}
      />
    </section>
  );
};

export default SurveyResponsesTab;
